import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { cn } from "../../../common/u1/cn";
import { CompanyMark, hasAssetUrl } from "../../../common/u1/CompanyMark";
import { StatusBadge } from "../../../common/u1/StatusBadge";
import { getCountryByCode } from "../../../../utils/Helper";
import { formatDateWithOrdinal } from "../../../../global_functions/global";
import { StatusGlyph, BuildingIcon, ClockIcon, ShieldIcon, LockIcon } from "../../../../assets/ui/icons";
import { profileStatus, VERIFIED, CLAIMED } from "./profileStatus";
import { EndorsementListModal } from "./EndorsementListModal";
import { profileDepth, endorsementView, viewerIsMember } from "./profileDepth";
import { useGetEndorsementsQuery } from "../../../../store/Company/companyApiSlice";
import { selectActiveCompany, selectActiveCompanyId } from "../../../../store/activeCompanySlice";

/* The profile's masthead — the design's gradient band and identity block. */
export const ProfileHero = ({
  company,
  trevexId,
  actions,
  socials,
  /* The action bar — follow, claim, enterprise, buy. Rendered below the
     metrics so the identity block reads first. */
  children,
  className = "",
}) => {
  const status = profileStatus(company);
  const name = company?.registered_company_name || company?.company?.name || "";

  const state = trevexId
    ? company?.companyAddress?.[0]?.company_address_detailsID?.state
    : company?.company?.city;
  const country = getCountryByCode(
    trevexId ? company?.country_code : company?.company?.countrycode
  );
  const location = [state, country].filter(Boolean).join(", ");

  const employees = company?.number_of_employees;
  const established = company?.date_of_establishment;

  /* The uploaded banner, when there is one. */
  const banner = company?.banner;
  const [bannerFailed, setBannerFailed] = useState(false);
  useEffect(() => {
    setBannerFailed(false);
  }, [banner]);
  const showBanner = hasAssetUrl(banner) && !bannerFailed;

  return (
    /* NOT overflow-hidden: the More menu opens from inside this card, and clipping the card clipped the menu — which read as a z-index problem but was not one. */
    <div className={cn("relative z-[5] rounded-b-[16px] bg-white", className)}>
      {/* The band. Decorative — it carries no text of its own, so the image is `alt=""` and the whole block stays aria-hidden. The gradient is the base: it shows... */}
      <div
        aria-hidden="true"
        className="relative h-[100px] w-full overflow-hidden bg-[image:var(--profile-band)] sm:h-[118px]"
      >
        {showBanner && (
          <img
            src={banner}
            alt=""
            data-testid="profile-banner"
            className="h-full w-full object-cover"
            onError={() => setBannerFailed(true)}
          />
        )}
      </div>

      <div className="px-5 pb-6 pt-[26px] sm:px-7 sm:pt-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
          {/* On a phone the mark straddles the band's lower edge, as the design draws it: pulled up by half its height so the top half sits on the gradient and the name... */}
          <div data-testid="profile-mark" className="-mt-[46px] shrink-0 sm:mt-0">
            <CompanyMark
              company={{ name, companyID: company?.companyID, logo: company?.logo }}
              /* 88px — the masthead's mark is the page's subject, not a row's
                 marker, and the design sizes it against the 27px name beside
                 it. `lg` (64px) is left alone for the screens using it. */
              size="xl"
              className="ring-4 ring-white"
            />
          </div>

          <div className="min-w-0 grow basis-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="m-0 text-[24px] font-bold tracking-[-.01em] text-[var(--ink)] font-sans sm:text-[27px]">
                {name}
              </h1>
              {(status === VERIFIED || status === CLAIMED) && (
                <StatusBadge status={status} />
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13.5px] text-[var(--ink-2)] font-sans">
              {location && (
                <span className="inline-flex items-center gap-1.5">
                  {company?.country_code && (
                    <img
                      src={`https://flagsapi.com/${company.country_code}/flat/64.png`}
                      alt=""
                      aria-hidden="true"
                      className="h-4 w-4 object-cover"
                    />
                  )}
                  {location}
                </span>
              )}

              {/* Transcribed guards: each of these only shows when present. */}
              {employees && (
                <>
                  <Dot />
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[var(--muted)]">
                      <BuildingIcon size={14} />
                    </span>
                    {employees} employees
                  </span>
                </>
              )}

              {established && (
                <>
                  <Dot />
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[var(--muted)]">
                      <ClockIcon size={14} />
                    </span>
                    Established {formatDateWithOrdinal(established)}
                  </span>
                </>
              )}
            </div>

            {/* The one line that explains what the badge means. Only drawn for
                a status that has something to explain. */}
            {(status === VERIFIED || status === CLAIMED) && (
              <p className="mt-2.5 mb-0 flex items-start gap-2 text-[13px] text-[var(--muted)] font-sans">
                <span className="mt-px shrink-0 text-[var(--success)]">
                  <StatusGlyph kind={status} size={14} />
                </span>
                {status === VERIFIED
                  ? "An authorized representative manages this company profile, and it has been verified by TREVEX."
                  : "An authorized representative manages this company profile."}
              </p>
            )}
          </div>

          {/* The live banner's social row and share control, passed in. */}
          {socials && <div className="shrink-0">{socials}</div>}
        </div>

        <ProfileMetrics company={company} />

        {/* `empty:hidden` because `children` is the ELEMENT, which is truthy even when the component inside renders null — as ProfileActionBar now does for an owner... */}
        {children && (
          <div className="mt-5 border-t border-[var(--line)] pt-5 empty:hidden">{children}</div>
        )}

        {actions && (
          <div className="mt-5 border-t border-[var(--line)] pt-5">{actions}</div>
        )}
      </div>
    </div>
  );
};

const Dot = () => (
  <span aria-hidden="true" className="text-[var(--muted-2)]">
    ·
  </span>
);

/* Profile depth and payment endorsements. */
export const ProfileMetrics = ({ company }) => {
  const { token } = useSelector((state) => state.auth);
  const activeCompany = useSelector(selectActiveCompany);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const companyId = company?.companyID;

  /* Transcribed skip conditions: no company or no session means no request. */
  const { data: view } = useGetEndorsementsQuery(
    { companyId, actingCompanyId: activeCompanyId, token },
    { skip: !companyId || !token }
  );

  const depth = profileDepth(company);
  const endorsements = endorsementView(view);

  /* The panel is always there now; what changes is whether it is filled or
     locked. See the note above for why the viewer's own membership decides
     that rather than the endpoint's answer. */
  const isMember = viewerIsMember(activeCompany);
  const locked = !isMember;
  /* "No Endorsement" covers both a member company with none on file and a
     target whose endorsements are not public at all — to a member, both mean
     there is nothing to read. */
  /* The list rides on the company-details payload (`endorsement_list`), so it
     is the count's source when present; the member-gated endpoint's count is
     the fallback for a payload that predates it. */
  const endorsementList = Array.isArray(company?.endorsement_list) ? company.endorsement_list : null;
  const endorsementCount = endorsementList
    ? endorsementList.length
    : endorsements.mode === "member"
    ? endorsements.count
    : 0;
  const none = endorsementCount === 0;
  const [listOpen, setListOpen] = useState(false);

  return (
    <div
      className={cn(
        "mt-5 grid grid-cols-1 gap-5 border-t border-[var(--line)] pt-5",
        /* Equal halves either side of the rule. */
        "sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
      )}
    >
      <div className="min-w-0">
        <p className="m-0 text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--muted)] font-sans">
          Profile depth
        </p>
        <p className="mt-1 mb-0 flex flex-wrap items-baseline gap-2.5">
          <span className="text-[22px] font-bold text-[var(--ink)] font-sans">
            {depth.label}
          </span>
          <span className="text-[13px] text-[var(--muted)] font-sans">{depth.note}</span>
        </p>

        <div
          role="meter"
          aria-valuenow={depth.filled}
          aria-valuemin={0}
          aria-valuemax={depth.total}
          aria-valuetext={depth.label}
          aria-label="Profile depth"
          className="mt-3 flex gap-2"
        >
          {Array.from({ length: depth.total }, (_, i) => (
            <span
              key={i}
              className={cn(
                "h-[5px] grow rounded-full",
                i < depth.filled ? "bg-[var(--coral)]" : "bg-[var(--slate-3)]"
              )}
            />
          ))}
        </div>
      </div>

      <span aria-hidden="true" className="hidden w-px bg-[var(--line)] sm:block" />

      <div className="min-w-0">
        <p className="m-0 text-[10.5px] font-bold uppercase tracking-[.07em] text-[var(--muted)] font-sans">
          Payment endorsements
        </p>

        {locked ? (
          /* Members only. Same geometry as the filled state and as the depth block opposite — a mark, a value line, a supporting line — so the row keeps its shape... */
          <>
            <p className="mt-1 mb-0 flex flex-wrap items-baseline gap-2.5">
              <span className="inline-flex items-baseline gap-2 text-[22px] font-bold text-[var(--muted)] font-sans">
                <span
                  aria-hidden="true"
                  className="inline-flex h-7 w-7 shrink-0 translate-y-[3px] items-center justify-center rounded-lg bg-[var(--slate-2)] text-[var(--muted)]"
                >
                  <LockIcon size={14} />
                </span>
                Members only
              </span>
            </p>
            <p className="mt-3 mb-0 text-[12.5px] leading-[1.5] text-[var(--muted)] font-sans">
              Membership shows you which counterparties vouch that this business
              pays on time.{" "}
              <Link
                to="/dashboard/membership"
                className="font-semibold text-[var(--coral)] hover:text-[var(--coral-deep)]"
              >
                Explore Membership
              </Link>
            </p>
          </>
        ) : (
          /* Built to match the depth block opposite: a value and a short reading of it on the same baseline, then a supporting row beneath. */
          <>
            <p className="mt-1 mb-0 flex flex-wrap items-baseline gap-2.5">
              <span
                className={cn(
                  "inline-flex items-baseline gap-2 text-[22px] font-bold font-sans",
                  none ? "text-[var(--muted-2)]" : "text-[var(--ink)]"
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "inline-flex h-7 w-7 shrink-0 translate-y-[3px] items-center justify-center rounded-lg",
                    none
                      ? "bg-[var(--slate-2)] text-[var(--muted-2)]"
                      : "bg-[var(--coral-soft)] text-[var(--coral)]"
                  )}
                >
                  <ShieldIcon size={14} />
                </span>
                {none ? "No Endorsement" : endorsementCount}
              </span>
              {!none && (
                <span className="text-[13px] text-[var(--muted)] font-sans">
                  {endorsementCount === 1 ? "endorsement" : "endorsements"}
                </span>
              )}
            </p>

            <p className="mt-3 mb-0 text-[12.5px] leading-[1.5] text-[var(--muted)] font-sans">
              {none
                ? "No one has vouched for this business's payment record yet."
                : "Vouching that this business pays on time."}{" "}
              {!none && endorsementList && (
                <button
                  type="button"
                  onClick={() => setListOpen(true)}
                  className="border-0 bg-transparent p-0 font-semibold text-[var(--coral)] hover:text-[var(--coral-deep)] cursor-pointer font-sans text-[12.5px]"
                >
                  View
                </button>
              )}
            </p>
          </>
        )}
      </div>

      {listOpen && (
        <EndorsementListModal endorsements={endorsementList} onClose={() => setListOpen(false)} />
      )}

    </div>
  );
};

export default ProfileHero;
